// Finance ▸ QuickBooks ▸ Needs attention (plan T8; ux review 2026-10-21).
// One list of everything between Flow and QuickBooks that needs a person —
// grouped, each with WHAT TO DO first, then what happened, who has to act,
// and the one button that can actually help:
//   • Retry — the same as Send on the Export tab;
//   • Check again — re-reads QuickBooks (a tax difference, a changed payment);
//   • I've done this — for things only a person can fix in QuickBooks.
// The server writes every sentence; this screen only lays them out.
//
// Retrying in the REAL books is switched off while the export lock is on; the
// button is disabled with the server's own reason, exactly like Send. Admins
// can look at the test company's list too, labelled "Test company only".
import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ClipboardCopy, ExternalLink, FlaskConical, RotateCw, Search } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import {
  errText,
  formatDate,
  type IssueCategory,
  type IssueOwner,
  type IssueRetryResult,
  type SyncIssue,
  type SyncIssueList,
} from "./types";

export function syncIssuesQueryKey(environment: "production" | "sandbox") {
  return ["qbo", "issues", environment] as const;
}

/** Chip colour per owner — the words carry the meaning; colour backs them up. */
const OWNER_TONE: Record<IssueOwner, string> = {
  you: "bg-ink text-white",
  jake: "bg-warn-bg text-warn",
  stripe: "bg-[#f1f1ee] text-ink",
  flow: "bg-ok-bg text-ok",
};

/** Menu paths in the sentences become links (ux review #13). */
const PATH_LINKS: Array<[string, string]> = [
  ["Export ▸ Invoices", "/finance/quickbooks?entity=invoices"],
  ["Export ▸ Card refunds", "/finance/quickbooks?entity=refunds"],
  ["Sales ▸ Website orders", "/sales/website-orders"],
  ["Sales ▸ Company stores", "/sales/company-stores"],
];

function withLinks(text: string): ReactNode {
  const parts: ReactNode[] = [];
  let rest = text;
  let n = 0;
  for (;;) {
    const hit = PATH_LINKS.filter(([p, to]) => to && rest.includes(p)).sort((a, b) => rest.indexOf(a[0]) - rest.indexOf(b[0]))[0];
    if (!hit) break;
    const at = rest.indexOf(hit[0]);
    parts.push(rest.slice(0, at));
    parts.push(
      <Link key={n++} to={hit[1]} className="font-medium underline underline-offset-2 hover:text-ink">
        {hit[0]}
      </Link>,
    );
    rest = rest.slice(at + hit[0].length);
  }
  parts.push(rest);
  return parts;
}

export function SyncIssuesPanel(props: {
  isAdmin: boolean;
  /** The production lock's reason when retrying in the real books is off. */
  retryBlockedReason: string | null;
  /** Page-level confirmation (survives the row disappearing — ux #10). */
  onConfirm: (message: string) => void;
}) {
  const [testCompany, setTestCompany] = useState(false);
  const environment = testCompany ? "sandbox" : "production";
  const issuesQ = useQuery({
    queryKey: syncIssuesQueryKey(environment),
    queryFn: () =>
      api<SyncIssueList>(`/api/finance/qbo/issues${environment === "sandbox" ? "?environment=sandbox" : ""}`),
  });
  const { hash } = useLocation();

  const data = issuesQ.data;
  const urgent = new Set(data?.urgent ?? []);
  const groups = data
    ? (Object.keys(data.categories) as IssueCategory[])
        .map((c) => ({ category: c, label: data.categories[c], issues: data.issues.filter((i) => i.category === c) }))
        .filter((g) => g.issues.length > 0)
    : [];

  // Arriving from "Why, and what to do →": scroll to that document's issue.
  useEffect(() => {
    if (!data || !hash.startsWith("#issue-")) return;
    const el = document.getElementById(hash.slice(1));
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [data, hash]);

  return (
    <section aria-label="Needs attention">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="max-w-[1100px]">
          <h2 className="text-xl font-semibold tracking-[-0.4px] text-ink">Needs attention</h2>
          <p className="mt-1 text-[15px] text-muted">
            Anything between Flow and QuickBooks that needs a person — what to do first, then what happened. Retry here
            does exactly what Send does on the Export tab. When something didn't go through, nothing was sent to
            QuickBooks and nothing was duplicated — unless it says Flow can't tell.
          </p>
        </div>
        {props.isAdmin && (
          <label className="inline-flex cursor-pointer items-center gap-2 text-[15px] text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 accent-ink"
              checked={testCompany}
              onChange={(e) => setTestCompany(e.target.checked)}
            />
            Show the test company instead
          </label>
        )}
      </div>

      {testCompany && (
        <div role="status" className="mb-5 flex items-start gap-3 rounded-md border border-warn/30 bg-warn-bg px-4 py-3 text-[15px] text-ink">
          <FlaskConical className="mt-0.5 h-4 w-4 shrink-0 text-warn" />
          <p>You're looking at the QuickBooks test company — nothing here is in your books.</p>
        </div>
      )}

      {issuesQ.isLoading ? (
        <div className="h-32 animate-pulse rounded-md border border-rule bg-white" aria-label="Loading" />
      ) : issuesQ.isError ? (
        <div role="alert" className="rounded-md border border-risk/30 bg-risk-bg p-4 text-[15px] text-risk">
          {errText(issuesQ.error, "Couldn't load what needs attention — try again in a moment.")}
        </div>
      ) : !data || data.count === 0 ? (
        <div className="rounded-md border border-rule bg-white px-6 py-12 text-center">
          <CheckCircle2 className="mx-auto mb-3 h-7 w-7 text-ok" />
          <p className="text-lg font-medium text-ink">Nothing needs you right now.</p>
          <p className="mt-1 text-[15px] text-muted">
            {environment === "sandbox"
              ? "The test company has nothing open."
              : "Everything sent to QuickBooks went through, and nothing is waiting too long."}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map((g) => (
            <div key={g.category}>
              <h3 className="mb-3 flex items-center gap-2 text-[17px] font-semibold text-ink">
                {urgent.has(g.category) ? (
                  <AlertTriangle className="h-[18px] w-[18px] text-risk" aria-hidden />
                ) : (
                  <span className="h-2 w-2 rounded-full bg-warn" aria-hidden />
                )}
                {g.label}
                <span className="text-[15px] font-normal text-muted">· {g.issues.length}</span>
              </h3>
              <ul className="overflow-hidden rounded-md border border-rule bg-white">
                {g.issues.map((issue) => (
                  <IssueRow
                    key={issue.key}
                    issue={issue}
                    ownerLabel={data.owners[issue.owner]}
                    highlighted={hash === `#issue-${issue.id}`}
                    onConfirm={props.onConfirm}
                    blockedReason={issue.environment === "production" ? props.retryBlockedReason : null}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function detailsForJake(issue: SyncIssue, ownerLabel: string): string {
  return [
    `${issue.title} — ${issue.headline}`,
    `Company: ${issue.environment === "sandbox" ? "QuickBooks test company" : "QuickBooks (your books)"}`,
    `Since: ${formatDate(issue.since)}${issue.attempts != null ? ` · tried ${issue.attempts} times` : ""}`,
    `What to do: ${issue.nextStep}`,
    `What happened: ${issue.reason}`,
    `Who: ${ownerLabel}`,
    `Technical: ${issue.technical ?? "(none recorded)"}`,
    `Flow record: ${issue.kind} ${issue.id}`,
  ].join("\n");
}

function IssueRow(props: {
  issue: SyncIssue;
  ownerLabel: string;
  highlighted: boolean;
  blockedReason: string | null;
  onConfirm: (message: string) => void;
}) {
  const { issue } = props;
  const queryClient = useQueryClient();
  const [answer, setAnswer] = useState<IssueRetryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const qs = issue.environment === "sandbox" ? "?environment=sandbox" : "";
  const retry = useMutation({
    mutationFn: () =>
      api<IssueRetryResult>(
        `/api/finance/qbo/issues/${issue.kind}/${issue.id}/retry${qs}${issue.action === "check_again" ? `${qs ? "&" : "?"}action=check_again` : ""}`,
        { method: "POST" },
      ),
    onSuccess: (r) => {
      setError(null);
      if (r.ok) {
        // The row may disappear on refresh — confirm at page level.
        props.onConfirm(`${issue.title}: ${r.message}`);
        setAnswer(null);
      } else {
        setAnswer(r);
      }
      void queryClient.invalidateQueries({ queryKey: ["qbo"] });
    },
    onError: (e) => setError(errText(e, "Nothing was sent to QuickBooks. Try again in a minute.")),
  });
  const handled = useMutation({
    mutationFn: () => api<{ ok: boolean; message: string }>(`/api/finance/qbo/issues/${issue.kind}/${issue.id}/handled${qs}`, { method: "POST" }),
    onSuccess: (r) => {
      props.onConfirm(r.message);
      void queryClient.invalidateQueries({ queryKey: ["qbo"] });
    },
    onError: (e) => setError(errText(e, "Couldn't save that — try again in a minute.")),
  });
  const isCheck = issue.action === "check_again";
  const Icon = isCheck ? Search : RotateCw;
  const actionLabel = isCheck ? "Check again" : "Retry";
  const actionHelp = isCheck
    ? "Reads QuickBooks again and clears this once it matches Flow — nothing is sent."
    : "Sends it to QuickBooks again — the same as Send on the Export tab. It never creates a duplicate.";

  return (
    <li
      id={`issue-${issue.id}`}
      className={cn(
        "scroll-mt-24 border-b border-[#e7e7e4] px-5 py-5 transition-colors last:border-0 sm:px-6",
        props.highlighted && "bg-warn-bg",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn("rounded px-2 py-0.5 text-[14px] font-medium", OWNER_TONE[issue.owner])}>{props.ownerLabel}</span>
            {issue.environment === "sandbox" && (
              <span className="rounded bg-warn-bg px-2 py-0.5 text-[14px] font-medium text-warn">Test company only</span>
            )}
          </div>
          <p className="mt-2 text-[17px] font-semibold text-ink">{issue.title}</p>
          <p className="mt-0.5 text-[16px] text-ink">{issue.headline}</p>
          <p className="mt-1 text-[14px] text-muted">
            Since {formatDate(issue.since)}
            {issue.attempts != null ? ` · tried ${issue.attempts} time${issue.attempts === 1 ? "" : "s"}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {issue.amount && <span className="text-[17px] font-medium tabular-nums text-ink">{issue.amount}</span>}
          {issue.action && (
            <Button
              type="button"
              variant="outline"
              size="md"
              disabled={!!props.blockedReason || retry.isPending}
              title={props.blockedReason ?? actionHelp}
              aria-label={`${actionLabel}: ${issue.title}`}
              onClick={() => retry.mutate()}
            >
              <Icon className="h-4 w-4" />
              {retry.isPending ? "Working…" : actionLabel}
            </Button>
          )}
          {issue.canMarkHandled && (
            <Button
              type="button"
              variant="outline"
              size="md"
              disabled={handled.isPending}
              title="Use this once you've fixed it in QuickBooks yourself. It comes back here if it changes again."
              aria-label={`I've done this: ${issue.title}`}
              onClick={() => handled.mutate()}
            >
              <CheckCircle2 className="h-4 w-4" />
              {handled.isPending ? "Saving…" : "I've done this"}
            </Button>
          )}
        </div>
      </div>

      <div className="mt-3 max-w-[1100px] rounded-md bg-canvas px-4 py-3">
        <p className="text-[16px] font-medium leading-relaxed text-ink">
          <span className="font-semibold">What to do: </span>
          {withLinks(issue.nextStep)}
        </p>
      </div>
      <p className="mt-2 max-w-[1100px] text-[15px] leading-relaxed text-muted">{issue.reason}</p>

      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[14px]">
        {issue.href && (
          <Link to={issue.href} className="inline-flex items-center gap-1 font-medium text-ink underline underline-offset-2">
            Open in Flow <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </Link>
        )}
        <button
          type="button"
          className="inline-flex items-center gap-1 text-muted underline underline-offset-2 hover:text-ink"
          onClick={() => {
            void navigator.clipboard?.writeText(detailsForJake(issue, props.ownerLabel)).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2500);
            });
          }}
        >
          <ClipboardCopy className="h-3.5 w-3.5" aria-hidden />
          {copied ? "Copied — paste it to Jake" : "Copy details for Jake"}
        </button>
        {issue.action && props.blockedReason && (
          <span className="text-muted">{actionLabel} is switched off until Jake turns sending on.</span>
        )}
      </div>

      {answer && (
        <p role="status" className="mt-3 rounded-md border border-warn/30 bg-warn-bg px-3 py-2 text-[15px] text-ink">
          {answer.message}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-3 rounded-md border border-risk/30 bg-risk-bg px-3 py-2 text-[15px] text-risk">
          {error}
        </p>
      )}
    </li>
  );
}
